import React from 'react';
import { Icon } from '@repo/ui';
import { Button, Card, CardHeader, CardBody, Input, Select } from '@repo/ui';
import type { UploadForm } from '../types';

// Standardized button styles
const BUTTON_STYLES = {
  primary: {
    variant: 'primary' as const,
    size: 'md' as const,
  },
  secondary: {
    variant: 'secondary' as const,
    size: 'md' as const,
  },
} as const;

const BUTTON_CLASSES = {
  group: 'gap-2',
} as const;

interface UploadTemplateFormProps {
  uploadForm: UploadForm;
  onUploadFormChange: (form: UploadForm) => void;
  onUpload: (uploadAndTest: boolean) => void;
  isUploading: boolean;
  isUploadingAndTesting: boolean;
}

export const UploadTemplateForm: React.FC<UploadTemplateFormProps> = ({
  uploadForm,
  onUploadFormChange,
  onUpload,
  isUploading,
  isUploadingAndTesting,
}) => {
  return (
    <div className="flex justify-center">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-dark-50">
            Upload New Template
          </h2>
          <p className="text-gray-600 dark:text-xtnd-dark-300 text-sm">
            Upload a Handlebars (.hbs) template file. Use "Upload & Test" to immediately test your
            template after uploading.
          </p>
        </CardHeader>
        <CardBody>
          <div className="space-y-6 text-gray-700 dark:text-xtnd-dark-50 text-sm font-medium">
            <div>
              <Input
                type="file"
                id="template-file"
                accept=".hbs"
                label="Template File (.hbs)"
                fullWidth
                className="text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-xtnd-light file:text-xtnd-dark hover:file:bg-xtnd-blue hover:file:text-white"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Template Name"
                value={uploadForm.templateName}
                onChange={e =>
                  onUploadFormChange({ ...uploadForm, templateName: e.target.value })
                }
                placeholder="e.g., Invoice Template"
                fullWidth
              />
              <Select
                label="Template Type"
                value={uploadForm.templateType}
                onChange={value =>
                  onUploadFormChange({
                    ...uploadForm,
                    templateType: value as 'pdf' | 'email' | 'document',
                  })
                }
                options={[
                  { value: 'pdf', label: 'PDF Template' },
                  { value: 'email', label: 'Email Template' },
                  { value: 'document', label: 'Document Template' },
                ]}
              />
              <Input
                label="Description"
                value={uploadForm.description}
                onChange={e => onUploadFormChange({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description of the template"
                fullWidth
              />
              <Input
                label="Tags (comma-separated)"
                value={uploadForm.tags}
                onChange={e => onUploadFormChange({ ...uploadForm, tags: e.target.value })}
                placeholder="invoice, billing, standard"
                fullWidth
              />
            </div>

            <div className={`flex justify-end ${BUTTON_CLASSES.group}`}>
              <Button
                onClick={() => onUpload(false)}
                disabled={!uploadForm.templateName || isUploading || isUploadingAndTesting}
                loading={isUploading}
                {...BUTTON_STYLES.secondary}
                leftIcon={<Icon name="HiArrowUp" className="h-4 w-4" />}
              >
                Upload
              </Button>
              <Button
                onClick={() => onUpload(true)}
                disabled={!uploadForm.templateName || isUploading || isUploadingAndTesting}
                loading={isUploadingAndTesting}
                {...BUTTON_STYLES.primary}
                leftIcon={<Icon name="HiPlay" className="h-4 w-4" />}
              >
                Upload & Test
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

